/*
 * Where to send someone once their account and organization exist. A page
 * that sends people here to sign up (a template's Remix button on
 * git.dodil.io, say) passes `?redirect_uri=`; it has to survive the email
 * check, the Google round trip and the org step, so it is kept for the
 * tab (sessionStorage: another tab's signup must not inherit it), for an hour.
 *
 * The finish hands the new tokens over in the URL fragment (signup signs in
 * server-side, so there is no Keycloak browser session to fall back on). So
 * only two first-party origins are ever accepted: the console's
 * (VITE_DEFAULT_REDIRECT's) and the partner site's (VITE_PARTNER_URL's).
 * Anything else is ignored and the default is used.
 */

const KEY = 'dodil_signup_return_to';
// Plain http only on this machine (local dev and the Arena serve the apps on
// 127.0.0.1, the same site as the local Keycloak). The origin must still match.
const LOOPBACK = new Set(['localhost', '127.0.0.1', '[::1]']);
const TTL_MS = 60 * 60 * 1000;

export function isSafeReturn(url: string, allowedOrigin: string): boolean {
    try {
        const u = new URL(url);
        const allowed = new URL(allowedOrigin);
        return u.origin === allowed.origin && (u.protocol === 'https:' || LOOPBACK.has(u.hostname));
    } catch {
        return false;
    }
}

const consoleOrigin = () => import.meta.env.VITE_DEFAULT_REDIRECT as string;
const partnerOrigin = () => (import.meta.env.VITE_PARTNER_URL as string | undefined) || '';

const isAllowed = (url: string) =>
    isSafeReturn(url, consoleOrigin()) || (!!partnerOrigin() && isSafeReturn(url, partnerOrigin()));

/** Whether the destination is the console. */
export function isConsole(url: string): boolean {
    return isSafeReturn(url, consoleOrigin());
}

/** Whether the destination is one of ours that takes the session in the fragment (the console, the partner site). */
export function takesSession(url: string): boolean {
    return isAllowed(url);
}

/** Call once at start: keep a valid `?redirect_uri=` for the end of signup. */
export function rememberReturnTo(search = window.location.search): void {
    const url = new URLSearchParams(search).get('redirect_uri');
    if (!url || !isAllowed(url)) return;
    try {
        sessionStorage.setItem(KEY, JSON.stringify({ url, at: Date.now() }));
    } catch { /* storage blocked: the default it is */ }
}

/** The remembered destination, else `fallback`. */
export function returnTo(fallback: string): string {
    try {
        const { url, at } = JSON.parse(sessionStorage.getItem(KEY) || 'null') ?? {};
        if (typeof url === 'string' && Date.now() - at < TTL_MS && isAllowed(url)) return url;
    } catch { /* fall through */ }
    return fallback;
}

export function forgetReturnTo(): void {
    try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}
